import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useLayoutEffect,
} from 'react';

import { getOffsetRect, getEffectiveScrollSpacing, assert } from './utils';

export const useIsomorphicLayoutEffect =
  typeof document !== 'undefined' ? useLayoutEffect : useEffect;

export type SnapCarouselResult = {
  readonly pages: number[][];
  readonly activePageIndex: number;
  readonly snapPointIndexes: Set<number>;
  readonly prev: () => void;
  readonly next: () => void;
  readonly goTo: (pageIndex: number, behavior?: ScrollBehavior) => void;
  readonly refresh: () => void;
  readonly scrollRef: (el: HTMLElement | null) => void;
  readonly scrollEl: HTMLElement | null;
};

export type SnapCarouselOptions = {
  readonly axis?: 'x' | 'y';
  readonly initialPages?: number[][];
  readonly initialIndex?: number;
};

type SnapSliderState = {
  readonly pages: number[][];
  readonly activePageIndex: number;
};

export const useSnapCarousel = ({
  axis = 'x',
  initialPages = [],
  initialIndex = 0,
}: SnapCarouselOptions = {}): SnapCarouselResult => {
  const isXAxis = axis === 'x';

  const dimension = isXAxis ? 'width' : 'height';
  const scrollDimension = isXAxis ? 'scrollWidth' : 'scrollHeight';
  const clientDimension = isXAxis ? 'clientWidth' : 'clientHeight';
  const nearSidePos = isXAxis ? 'left' : 'top';
  const farSidePos = isXAxis ? 'right' : 'bottom';
  const scrollPos = isXAxis ? 'scrollLeft' : 'scrollTop';

  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);

  const [{ pages, activePageIndex }, setCarouselState] =
    useState<SnapSliderState>({
      pages: initialPages,
      activePageIndex: initialIndex,
    });
  const refreshActivePage = useCallback(
    (pages: number[][]) => {
      if (!scrollEl) {
        return;
      }

      const hasScrolledToEnd =
        Math.floor(scrollEl[scrollDimension] - scrollEl[scrollPos]) <=
        scrollEl[clientDimension];

      if (hasScrolledToEnd) {
        setCarouselState({ pages, activePageIndex: pages.length - 1 });

        return;
      }

      const items = Array.from(scrollEl.children);
      const scrollPort = scrollEl.getBoundingClientRect();

      const offsets = pages.map((page) => {
        const leadIndex = page[0];

        const leadEl = items[leadIndex];

        assert(leadEl instanceof HTMLElement, 'Expected HTMLElement');

        const scrollSpacing = getEffectiveScrollSpacing(
          scrollEl,
          leadEl,
          nearSidePos
        );

        const rect = leadEl.getBoundingClientRect();

        const offset =
          rect[nearSidePos] - scrollPort[nearSidePos] - scrollSpacing;
        return Math.abs(offset);
      });

      const minOffset = Math.min(...offsets);

      const nextActivePageIndex = offsets.indexOf(minOffset);

      setCarouselState({ pages, activePageIndex: nextActivePageIndex });
    },
    [scrollEl, clientDimension, nearSidePos, scrollDimension, scrollPos]
  );

  const refresh = useCallback(() => {
    if (!scrollEl) {
      return;
    }

    const items = Array.from(scrollEl.children);

    const scrollPort = scrollEl.getBoundingClientRect();

    let currPageStartPos: number;

    const pages = items.reduce<number[][]>((acc, item, i) => {
      assert(item instanceof HTMLElement, 'Expected HTMLElement');
      const currPage = acc[acc.length - 1];

      const rect = getOffsetRect(item, item.parentElement);

      if (
        !currPage ||
        rect[farSidePos] - currPageStartPos > Math.ceil(scrollPort[dimension])
      ) {
        acc.push([i]);

        const scrollSpacing = getEffectiveScrollSpacing(
          scrollEl,
          item,
          nearSidePos
        );

        currPageStartPos = rect[nearSidePos] - scrollSpacing;
      } else {
        currPage.push(i);
      }

      return acc;
    }, []);

    refreshActivePage(pages);
  }, [refreshActivePage, scrollEl, dimension, farSidePos, nearSidePos]);

  useIsomorphicLayoutEffect(() => {
    refresh();
  }, [refresh]);

  // Add effect to set initial scroll position
  useIsomorphicLayoutEffect(() => {
    if (!scrollEl || !pages.length) {
      console.warn(
        'Missing scrollEl or pages array, skipping scroll adjustment'
      );
      return;
    }

    const page = pages[initialIndex];
    if (!page) {
      console.warn(
        `No page found at index ${initialIndex}, skipping scroll adjustment`
      );
      return;
    }

    const items = Array.from(scrollEl.children);
    const leadIndex = page[0];
    const leadEl = items[leadIndex];

    if (!leadEl) {
      console.warn(
        `No element found at leadIndex ${leadIndex}, skipping scroll adjustment`
      );
      return;
    }

    if (!(leadEl instanceof HTMLElement)) {
      console.warn('leadEl is not an HTMLElement, skipping scroll adjustment');
      return;
    }

    const scrollSpacing = getEffectiveScrollSpacing(
      scrollEl,
      leadEl,
      nearSidePos
    );

    scrollEl.scrollTo({
      behavior: 'auto',
      [nearSidePos]:
        getOffsetRect(leadEl, leadEl.parentElement)[nearSidePos] -
        scrollSpacing,
    });
  }, [scrollEl, pages, initialIndex, nearSidePos]);

  useEffect(() => {
    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);

    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (!scrollEl) {
      return;
    }

    const handle = () => {
      refreshActivePage(pages);
    };

    scrollEl.addEventListener('scroll', handle);

    return () => {
      scrollEl.removeEventListener('scroll', handle);
    };
  }, [refreshActivePage, pages, scrollEl]);

  const handleGoTo = (index: number, behavior: ScrollBehavior = 'smooth') => {
    if (!scrollEl) {
      return;
    }

    const page = pages[index];

    if (!page) {
      return;
    }

    const items = Array.from(scrollEl.children);

    const leadIndex: number | undefined = page[0];

    const leadEl: Element | undefined = items[leadIndex];

    if (!(leadEl instanceof HTMLElement)) {
      return;
    }

    const scrollSpacing = getEffectiveScrollSpacing(
      scrollEl,
      leadEl,
      nearSidePos
    );

    scrollEl.scrollTo({
      behavior: behavior,
      [nearSidePos]:
        getOffsetRect(leadEl, leadEl.parentElement)[nearSidePos] -
        scrollSpacing,
    });
  };

  const handlePrev = () => {
    handleGoTo(activePageIndex - 1);
  };

  const handleNext = () => {
    handleGoTo(activePageIndex + 1);
  };

  const snapPointIndexes = useMemo(
    () => new Set(pages.map((page) => page[0])),
    [pages]
  );

  return {
    prev: handlePrev,
    next: handleNext,
    goTo: handleGoTo,
    refresh: refresh,
    pages: pages,
    activePageIndex: activePageIndex,
    snapPointIndexes: snapPointIndexes,
    scrollRef: setScrollEl,
    scrollEl: scrollEl,
  };
};
